import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Linkedin, Youtube, ArrowRight, CheckCircle2 } from "lucide-react";
import { Logo } from "./Logo";
import { SOCIALS } from "../lib/data";
import { sendForm } from "../lib/forms";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const botRef = useRef(null); // hidden spam trap: humans never tick it

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await sendForm({
        subject: `New newsletter sign-up: ${email}`,
        from_name: "Ethikally website",
        email,
        message: `${email} signed up for the Ethikally newsletter from the website footer.`,
        botcheck: botRef.current?.checked || false,
      });
      toast.success("Subscribed. Welcome to Ethikally.");
      setEmail("");
      setSubscribed(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-navy text-cream relative z-10" data-testid="site-footer">
      {/* Newsletter */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 border-b border-cream/10">
        <div className="grid md:grid-cols-2 gap-10 items-end">
          <div>
            <span className="font-mono text-xs tracking-[0.22em] uppercase text-forest">Newsletter</span>
            <h3 className="font-heading text-3xl md:text-4xl mt-4 leading-tight">
              Finance made easy.
            </h3>
          </div>
          {subscribed ? (
            <div
              data-testid="newsletter-success"
              className="flex items-center gap-3 rounded-2xl bg-forest/15 border border-forest/40 px-6 py-5"
            >
              <CheckCircle2 className="text-[#5FC08A] shrink-0" size={26} />
              <div>
                <p className="text-cream font-medium">You're on the list.</p>
                <p className="text-cream/60 text-sm">Thanks for subscribing — clear finance, straight to your inbox.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={subscribe} data-testid="newsletter-form">
              {/* Spam trap, invisible to visitors */}
              <input
                ref={botRef}
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ display: "none" }}
              />
              <div className="flex items-end gap-4">
              <div className="flex-1">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="newsletter-email-input"
                  className="w-full bg-transparent border-b border-cream/30 focus:border-forest outline-none py-3 text-cream placeholder:text-cream/40 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="rounded-full bg-forest text-cream h-12 w-12 flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50 shrink-0"
                aria-label="Subscribe"
              >
                <ArrowRight size={20} />
              </button>
              </div>
              <p className="mt-3 text-xs text-cream/50">
                We'll use your email only to send the newsletter. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Main */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1.5 flex items-center justify-center">
              <Logo className="h-8 w-8" />
            </div>
            <span className="font-heading text-2xl font-semibold text-cream">Ethikally</span>
          </div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-forest mt-4">
            Vision · Value · Growth
          </p>
          <div className="flex gap-4 mt-8">
            <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="h-10 w-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-forest hover:border-forest transition-colors">
              <Instagram size={18} />
            </a>
            <a href="https://www.linkedin.com/company/ethikally/" target="_blank" rel="noreferrer" data-testid="footer-linkedin" className="h-10 w-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-forest hover:border-forest transition-colors">
              <Linkedin size={18} />
            </a>
            <a href={SOCIALS.youtube} target="_blank" rel="noreferrer" data-testid="footer-youtube" className="h-10 w-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-forest hover:border-forest transition-colors">
              <Youtube size={18} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-mono text-xs tracking-[0.2em] uppercase text-cream/50 mb-5">Navigate</h4>
          <ul className="space-y-3 font-body text-cream/80">
            <li><Link to="/" className="hover:text-forest transition-colors">Home</Link></li>
            <li><Link to="/about" className="hover:text-forest transition-colors">About</Link></li>
            <li><Link to="/services" className="hover:text-forest transition-colors">Services</Link></li>
            <li><Link to="/insights" className="hover:text-forest transition-colors">Insights</Link></li>
            <li><Link to="/contact" className="hover:text-forest transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-mono text-xs tracking-[0.2em] uppercase text-cream/50 mb-5">Reach us</h4>
          <a href={`mailto:${SOCIALS.email}`} className="font-heading text-2xl hover:text-forest transition-colors">
            {SOCIALS.email}
          </a>
          <p className="text-cream/60 text-sm mt-3">@ethikally on Instagram & YouTube</p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 border-t border-cream/10">
        <p className="text-cream/40 text-xs font-mono tracking-wide">© Ethikally 2026 · Vision · Value · Growth</p>
      </div>
    </footer>
  );
};
